import { createContext, ReactNode, useEffect, useState } from "react";
import { decodeToken } from "react-jwt";
import { cpfCnpjNoMask } from "../functions/mask";
import api from "../services/api";
import { usePluginspace } from "../hooks/usePluginspace";

type TokenSellerType = {
  name: string;
  id: number;
  email: string;
  identifier: string;
  pluginAdm: number;
  pluginspaceId: number;
  plugintype: string;
  profile: string;
  exp: number;
};

type CustomerSellerType = {
  id: number;
  cpf: string;
  name: string;
  phone: string;
  guid: string;
  url: string;
};

type SellerUser = {
  cpf: string;
  email: string;
  id: number;
  name: string;
  numberPhone: string;
  pluginspaceId: number;
  sellerCode?: any;
  image?: string;
};

type SellerContextType = {
  customerToSeller: CustomerSellerType | null;
  setCustomerToSeller: (value: CustomerSellerType | null) => void;
  sellerLogin: (
    email: string,
    password: string,
    cpfOrCnpj: string,
    pluginspaceName: string,
  ) => Promise<boolean>;
  getTokenSeller: () => TokenSellerType | null;
  getTokenSellertoRequest: () => string | null;
  getSellerUser: () => Promise<SellerUser | null>;
  sellerUser: SellerUser | null;
};

type SellerContextProviderProps = {
  children: ReactNode;
};

export const SellerContext = createContext({} as SellerContextType);

export function SellerContextProvider(props: SellerContextProviderProps) {
  const { getToken } = usePluginspace();

  const [customerToSeller, setCustomerToSeller] =
    useState<CustomerSellerType | null>(null);
  const [sellerUser, setSellerUser] = useState<SellerUser | null>(null);
  async function sellerLogin(
    email: string,
    password: string,
    cpfOrCnpj: string,
    pluginspaceName: string,
  ) {
    return await api
      .post(`/api/token/login_seller`, {
        email,
        password,
        pluginspaceName,
      })
      .then((response) => {
        if (response.data.accessToken) {
          const tkNoSalt = response.data.accessToken
            .split(process.env.REACT_APP_JWT_SALT)
            .join("");
          const tk: TokenSellerType | null = decodeToken(tkNoSalt);
          if (tk && cpfCnpjNoMask(cpfOrCnpj) === tk.identifier) {
            localStorage.setItem("seller-tk", response.data.accessToken);
            getSellerUser();
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      })
      .catch(() => {
        return false;
      });
  }

  function getTokenSeller() {
    const sellerTk = localStorage.getItem("seller-tk");
    if (sellerTk && process.env.REACT_APP_JWT_SALT) {
      const tkNoSalt = sellerTk.split(process.env.REACT_APP_JWT_SALT).join("");
      const tk: TokenSellerType | null = decodeToken(tkNoSalt);
      return tk;
    } else {
      return null;
    }
  }

  function getTokenSellertoRequest() {
    const sellerTk = localStorage.getItem("seller-tk");
    if (sellerTk && process.env.REACT_APP_JWT_SALT) {
      return sellerTk;
    } else {
      return null;
    }
  }

  async function getSellerUser() {
    if (sellerUser) {
      return sellerUser;
    }

    const sellerTk = getTokenSeller();
    if (sellerTk) {
      return await api
        .get(`/api/users/${sellerTk.id}`, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((response) => {
          if (response.data) {
            const data: SellerUser = {
              cpf: response.data.cpf,
              email: response.data.email,
              id: response.data.id,
              name: response.data.name,
              numberPhone: response.data.numberPhone,
              pluginspaceId: response.data.pluginspaceId,
              image: response.data.image,
            };

            setSellerUser(data);
            return data;
          } else {
            return null;
          }
        })
        .catch(() => {
          return null;
        });
    }

    return null;
  }

  useEffect(() => {
    getSellerUser();
  }, []);

  return (
    <SellerContext.Provider
      value={{
        customerToSeller,
        setCustomerToSeller,
        sellerLogin,
        getTokenSeller,
        getTokenSellertoRequest,
        sellerUser,
        getSellerUser,
      }}
    >
      {props.children}
    </SellerContext.Provider>
  );
}
